import React, { useState } from 'react';
import PageLayout from '../components/PageLayout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import { ChevronDown, ChevronUp, Shield, FileText, Scale, AlertCircle } from 'lucide-react';

const Section = ({ title, icon: Icon, color, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <Card className="fly-card mb-4 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
            <Icon className="w-4 h-4" style={{ color }} />
          </div>
          <h2 className="font-semibold text-base" style={{ fontFamily: 'Space Grotesk' }}>{title}</h2>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </Card>
  );
};

export default function Terms() {
  return (
    <PageLayout>
      <SEOHead
        title="Terms of Service — Fly N Out"
        description="Fly N Out Terms of Service. Understand your rights, responsibilities, and the rules that govern your use of our global travel community platform."
        url="/terms"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Scale className="w-7 h-7 text-[#1E3A8A]" />
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Terms of Service</h1>
          </div>
          <p className="text-muted-foreground">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
          <div className="mt-4 p-4 rounded-xl bg-[#1E3A8A]/8 border border-[#1E3A8A]/20">
            <p className="text-sm leading-relaxed">
              By using Fly N Out, you agree to these terms. Please read them carefully. These terms govern your use of flynout.com and all associated mobile applications and services.
            </p>
          </div>
        </div>

        <Section title="Acceptance of Terms" icon={FileText} color="#1E3A8A">
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>By accessing or using Fly N Out ("the Service"), you confirm that you are at least 16 years of age and agree to be bound by these Terms of Service and our Privacy Policy.</p>
            <p>If you are using the Service on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these terms.</p>
            <p>We may update these terms from time to time. Continued use of the Service after changes constitutes acceptance of the new terms. We will notify registered users of significant changes via email or in-app notification.</p>
          </div>
        </Section>

        <Section title="Your Account" icon={Shield} color="#FF4500">
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.</p>
            <p>You agree to provide accurate, current, and complete information when registering and to keep it updated. You must not create accounts using false information or impersonate others.</p>
            <p>We reserve the right to suspend or terminate accounts that violate these terms, engage in fraudulent activity, or otherwise harm the community or platform integrity.</p>
            <p><strong className="text-foreground">Public Identity:</strong> Your displayed name on Fly N Out is shortened for privacy. Your full name is never shown publicly — only your first name and last initial are visible to other community members.</p>
          </div>
        </Section>

        <Section title="Community Rules & Content" icon={AlertCircle} color="#10B981">
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>Fly N Out is a community for travelers. You agree not to post content that is:</p>
            <ul className="list-disc ml-5 space-y-1.5">
              <li>Harassing, threatening, or abusive toward other users</li>
              <li>False, misleading, or intended to deceive (including fake flight information)</li>
              <li>Infringing on any third party's intellectual property rights</li>
              <li>Spam, promotional content without authorization, or irrelevant to travel</li>
              <li>Violating any applicable laws or regulations</li>
              <li>Containing personal information of others without their consent</li>
            </ul>
            <p>You retain ownership of content you post, but grant Fly N Out a non-exclusive, worldwide, royalty-free license to use, display, and distribute your content in connection with the Service.</p>
            <p>We reserve the right to remove any content that violates these rules and to ban users who repeatedly violate our community standards.</p>
          </div>
        </Section>

        <Section title="Premium Subscription" icon={Scale} color="#FF4500">
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>Fly N Out Club is a premium subscription at SAR 29/month, processed securely via Moyasar. By subscribing, you authorize recurring monthly charges to your selected payment method.</p>
            <p>You may cancel your subscription at any time. Cancellation takes effect at the end of the current billing period. We do not provide refunds for partial subscription periods except where required by applicable law.</p>
            <p>Premium features are subject to change. We will provide reasonable notice before removing features that existing subscribers are actively using.</p>
          </div>
        </Section>

        <Section title="Limitation of Liability" icon={Scale} color="#1E3A8A">
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>Fly N Out provides community-sourced travel information and is not responsible for the accuracy of user-generated content, including flight status updates, gate changes, or airport tips.</p>
            <p>Always verify critical travel information with official airline and airport sources. Community posts are for informational purposes only.</p>
            <p>To the maximum extent permitted by law, Fly N Out shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service.</p>
          </div>
        </Section>

        <Section title="Governing Law" icon={Scale} color="#10B981">
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>These Terms are governed by the laws of the Kingdom of Saudi Arabia. Any disputes will be resolved in the courts of Riyadh, Saudi Arabia.</p>
            <p>If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.</p>
            <p>For questions about these Terms, contact us at <a href="mailto:info@thesaudigate.com" className="text-[#FF4500] hover:underline">info@thesaudigate.com</a></p>
          </div>
        </Section>

        <div className="mt-6 p-4 rounded-xl bg-muted/50 text-center">
          <p className="text-xs text-muted-foreground">
            By using Fly N Out, you acknowledge that you have read, understood, and agree to these Terms of Service.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Questions? <a href="/contact" className="text-[#FF4500] hover:underline">Contact our support team</a>
          </p>
        </div>
      </div>
    </PageLayout>
  );
}
